// src/components/ui/Card/Card.tsx
import React, {useState, useEffect, useMemo} from 'react';
import {IonIcon, IonProgressBar, IonSpinner} from '@ionic/react';
import {chevronForwardOutline} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// FORGE BACKGROUND COMPONENT (Animated Blue/Gold)
// ═══════════════════════════════════════════════════════════════════

interface ForgeParticle {
    id: number;
    x: number;
    delay: number;
    duration: number;
}

/**
 * Animated forge-inspired background with floating gold particles.
 * Used for hero cards and featured sections.
 */
export const ForgeBackground: React.FC<{
    /** Whether to show animated particles */
    showParticles?: boolean;
    /** Number of particles (default 12) */
    particleCount?: number;
    /** Intensity of the gold glow (0-1, default 0.15) */
    glowIntensity?: number;
    /** Whether to show texture grid lines */
    showTexture?: boolean;
}> = ({
    showParticles = true,
    particleCount = 12,
    glowIntensity = 0.15,
    showTexture = true,
}) => {
    const [particles, setParticles] = useState<ForgeParticle[]>([]);

    useEffect(() => {
        if (showParticles) {
            const newParticles = Array.from({length: particleCount}, (_, i) => ({
                id: i,
                x: Math.random() * 100,
                delay: Math.random() * 3,
                duration: 2 + Math.random() * 2,
            }));
            setParticles(newParticles);
        }
    }, [showParticles, particleCount]);

    return (
        <>
            {/* Main gradient background */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(175deg, var(--kff-foundry-blue) 0%, #0a1a2a 60%, #050d14 100%)',
                borderRadius: 'inherit',
            }} />

            {/* Atmospheric gold glow from bottom */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(ellipse 80% 50% at 50% 100%, rgba(203, 160, 82, ${glowIntensity}) 0%, transparent 70%)`,
                pointerEvents: 'none',
                borderRadius: 'inherit',
            }} />

            {/* Steel texture grid lines */}
            {showTexture && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `
                        repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(255,255,255,0.02) 50px),
                        repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(255,255,255,0.02) 50px)
                    `,
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                }} />
            )}

            {/* Floating gold particles */}
            {showParticles && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                }}>
                    {particles.map(p => (
                        <div
                            key={p.id}
                            className="forge-particle"
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: `${p.x}%`,
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                background: 'var(--kff-forge-gold)',
                                boxShadow: '0 0 6px var(--kff-forge-gold), 0 0 12px rgba(203, 160, 82, 0.5)',
                                animation: `forge-float-up ${p.duration}s ease-out infinite`,
                                animationDelay: `${p.delay}s`,
                                opacity: 0,
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Keyframes for particle animation */}
            <style>{`
                @keyframes forge-float-up {
                    0% { transform: translateY(0); opacity: 0; }
                    10% { opacity: 0.8; }
                    90% { opacity: 0.3; }
                    100% { transform: translateY(-200px); opacity: 0; }
                }
            `}</style>
        </>
    );
};

ForgeBackground.displayName = 'ForgeBackground';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Card visual variants */
export type CardVariant =
    | 'default'       // Standard card with subtle background
    | 'elevated'      // Raised card with prominent shadow
    | 'hero'          // Featured card with gradient (like Net Worth)
    | 'outlined'      // Border-only, no background fill
    | 'ghost';        // Minimal, transparent background

/** Animated background types for cards */
export type AnimatedBackground =
    | 'none'          // No animated background
    | 'forge';        // Blue/gold forge aesthetic with floating particles

/** Card accent colors for left border or highlights */
export type CardAccent =
    | 'none'
    | 'primary'       // Foundry Blue
    | 'gold'          // Forge Gold
    | 'success'       // Timberline Green
    | 'danger'        // Expense Red
    | 'transfer';     // Purple

/** Card size presets */
export type CardSize = 'sm' | 'md' | 'lg';

/** Stat item for StatCard */
export interface StatItem {
    label: string;
    value: string | number;
    /** Optional: 'positive' | 'negative' | 'neutral' for color coding */
    sentiment?: 'positive' | 'negative' | 'neutral';
    /** Optional sublabel like "vs last month" */
    sublabel?: string;
}

/** Progress bar configuration */
export interface ProgressConfig {
    value: number;        // 0-1
    max?: number;         // For display like "$500 / $1000"
    label?: string;       // e.g., "75% utilized"
    color?: 'auto' | 'primary' | 'success' | 'warning' | 'danger';
}

/** Base Card props */
export interface CardProps {
    /** Visual variant */
    variant?: CardVariant;
    /** Animated background effect (works best with 'hero' variant) */
    animatedBackground?: AnimatedBackground;
    /** Left accent border color */
    accent?: CardAccent;
    /** Size preset */
    size?: CardSize;
    /** Card title */
    title?: string;
    /** Card subtitle */
    subtitle?: string;
    /** Icon (ionicon name) or custom React node */
    icon?: string | React.ReactNode;
    /** Icon background color (for icon badge) */
    iconColor?: CardAccent;
    /** Show chevron indicator for clickable cards */
    showChevron?: boolean;
    /** Make card interactive/clickable */
    interactive?: boolean;
    /** Click handler */
    onClick?: () => void;
    /** Loading state */
    loading?: boolean;
    /** Disabled state */
    disabled?: boolean;
    /** Custom className */
    className?: string;
    /** Custom inline styles */
    style?: React.CSSProperties;
    /** Card content */
    children?: React.ReactNode;
    /** ARIA label */
    ariaLabel?: string;
}

/** StatCard specific props */
export interface StatCardProps extends Omit<CardProps, 'children'> {
    /** Primary large value to display */
    primaryValue: string | number;
    /** Primary value sentiment for coloring */
    primarySentiment?: 'positive' | 'negative' | 'neutral';
    /** Array of stat items to display in a row */
    stats?: StatItem[];
    /** Progress bar configuration */
    progress?: ProgressConfig;
}

/** AccountCard specific props (based on Account schema) */
export interface AccountCardProps extends Omit<CardProps, 'children' | 'title' | 'subtitle'> {
    /** Account name */
    name: string;
    /** Account mask (last 4 digits) */
    mask?: string;
    /** Institution name */
    institution?: string;
    /** Logo URL */
    logoUrl?: string;
    /** Account type for icon/color defaults */
    accountType?: 'checking' | 'savings' | 'credit' | 'loan' | 'investment';
    /** Account subtype (e.g., 'heloc' for loans) */
    accountSubtype?: string;
    /** Current balance */
    balance: number;
    /** Credit limit (for credit cards and HELOCs) */
    creditLimit?: number;
    /** Original balance (for loans - used for payoff progress) */
    originalBalance?: number;
    /** Interest rate / APR (for credit cards and loans) */
    interestRate?: number;
    /** APY (for savings/checking accounts) */
    apy?: number;
    /** Whether this is a debt account */
    isDebt?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// STYLE CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const SIZE_CONFIG: Record<CardSize, {
    padding: string;
    titleSize: number;
    subtitleSize: number;
    borderRadius: number;
    iconSize: number;
    iconBadgeSize: number;
}> = {
    sm: {
        padding: '16px',
        titleSize: 14,
        subtitleSize: 11,
        borderRadius: 12,
        iconSize: 18,
        iconBadgeSize: 36,
    },
    md: {
        padding: '20px',
        titleSize: 16,
        subtitleSize: 12,
        borderRadius: 16,
        iconSize: 20,
        iconBadgeSize: 44,
    },
    lg: {
        padding: '24px',
        titleSize: 18,
        subtitleSize: 13,
        borderRadius: 20,
        iconSize: 24,
        iconBadgeSize: 52,
    },
};

const ACCENT_COLORS: Record<CardAccent, string> = {
    none: 'transparent',
    primary: 'var(--kff-foundry-blue)',
    gold: 'var(--kff-forge-gold)',
    success: 'var(--ff-income, var(--kff-timberline-green))',
    danger: 'var(--ff-expense, var(--ion-color-danger))',
    transfer: 'var(--ff-transfer, #a78bfa)',
};

const SENTIMENT_COLORS = {
    positive: 'var(--ff-income)',
    negative: 'var(--ff-expense)',
    neutral: 'var(--ion-text-color)',
};

// ═══════════════════════════════════════════════════════════════════
// BASE CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Card: React.FC<CardProps> = ({
    variant = 'default',
    animatedBackground = 'none',
    accent = 'none',
    size = 'md',
    title,
    subtitle,
    icon,
    iconColor = 'primary',
    showChevron = false,
    interactive = false,
    onClick,
    loading = false,
    disabled = false,
    className,
    style,
    children,
    ariaLabel,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const sizeConfig = SIZE_CONFIG[size];
    const isClickable = interactive || !!onClick;

    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            position: 'relative',
            padding: sizeConfig.padding,
            borderRadius: sizeConfig.borderRadius,
            transition: 'all 0.2s ease',
            cursor: isClickable && !disabled ? 'pointer' : 'default',
            opacity: disabled ? 0.5 : 1,
            borderLeft: accent !== 'none' ? `4px solid ${ACCENT_COLORS[accent]}` : undefined,
        };

        // Variant-specific styles
        switch (variant) {
            case 'default':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    boxShadow: isHovered && isClickable
                        ? '0 8px 24px rgba(0,0,0,0.15)'
                        : '0 4px 12px rgba(0,0,0,0.08)',
                    transform: isHovered && isClickable ? 'translateY(-2px)' : 'none',
                };
            case 'elevated':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    boxShadow: isHovered && isClickable
                        ? '0 12px 32px rgba(0,0,0,0.2)'
                        : '0 8px 24px rgba(0,0,0,0.12)',
                    transform: isHovered && isClickable ? 'translateY(-3px)' : 'none',
                };
            case 'hero':
                return {
                    ...base,
                    // When using animated background, the ForgeBackground handles the visuals
                    background: animatedBackground === 'forge'
                        ? 'transparent'
                        : 'linear-gradient(135deg, var(--kff-foundry-blue) 0%, #0f2942 100%)',
                    boxShadow: '0 8px 32px rgba(27, 59, 90, 0.4)',
                    color: '#ffffff',
                    overflow: 'hidden', // Needed for particle animation
                };
            case 'outlined':
                return {
                    ...base,
                    background: 'transparent',
                    border: '1px solid var(--ion-border-color)',
                    boxShadow: isHovered && isClickable
                        ? '0 4px 12px rgba(0,0,0,0.08)'
                        : 'none',
                };
            case 'ghost':
                return {
                    ...base,
                    background: isHovered && isClickable
                        ? 'var(--ion-color-step-50)'
                        : 'transparent',
                    boxShadow: 'none',
                };
            default:
                return base;
        }
    };

    const getFocusRingStyles = (): React.CSSProperties | undefined => {
        if (!isFocused) return undefined;
        return {
            position: 'absolute',
            inset: -2,
            borderRadius: sizeConfig.borderRadius + 2,
            border: '2px solid var(--kff-forge-gold)',
            pointerEvents: 'none',
        };
    };

    const renderIcon = () => {
        if (!icon) return null;

        const isStringIcon = typeof icon === 'string';

        return (
            <div style={{
                width: sizeConfig.iconBadgeSize,
                height: sizeConfig.iconBadgeSize,
                borderRadius: sizeConfig.borderRadius - 4,
                background: variant === 'hero'
                    ? 'rgba(255,255,255,0.15)'
                    : ACCENT_COLORS[iconColor],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                {isStringIcon ? (
                    <IonIcon
                        icon={icon}
                        style={{
                            fontSize: sizeConfig.iconSize,
                            color: variant === 'hero' ? '#ffffff' : '#ffffff',
                        }}
                    />
                ) : icon}
            </div>
        );
    };

    const Element = isClickable ? 'button' : 'div';

    return (
        <Element
            onClick={!disabled ? onClick : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-disabled={disabled}
            className={className}
            style={{
                ...getContainerStyles(),
                ...style,
                border: Element === 'button' ? 'none' : undefined,
                width: Element === 'button' ? '100%' : undefined,
                textAlign: Element === 'button' ? 'left' : undefined,
                font: Element === 'button' ? 'inherit' : undefined,
            }}
        >
            {/* Animated background (forge) */}
            {animatedBackground === 'forge' && (
                <ForgeBackground
                    showParticles={true}
                    particleCount={10}
                    glowIntensity={0.2}
                    showTexture={true}
                />
            )}

            {/* Focus ring */}
            {isFocused && <span style={getFocusRingStyles()} aria-hidden="true" />}

            {/* Loading overlay */}
            {loading && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: sizeConfig.borderRadius,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                }}>
                    <IonSpinner name="crescent" color="light" />
                </div>
            )}

            {/* Card content wrapper - ensures content appears above animated background */}
            <div style={{
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Header */}
                {(title || icon) && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: children ? 16 : 0,
                    }}>
                        {renderIcon()}
                        <div style={{flex: 1, minWidth: 0}}>
                            {title && (
                                <h3 style={{
                                    fontFamily: 'var(--kff-heading-font)',
                                    fontSize: sizeConfig.titleSize,
                                    fontWeight: 600,
                                    margin: 0,
                                    color: variant === 'hero' ? 'rgba(255,255,255,0.9)' : 'var(--ion-text-color)',
                                }}>
                                    {title}
                                </h3>
                            )}
                            {subtitle && (
                                <p style={{
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: sizeConfig.subtitleSize,
                                    margin: '2px 0 0',
                                    color: variant === 'hero' ? 'rgba(255,255,255,0.6)' : 'var(--ion-color-medium)',
                                }}>
                                    {subtitle}
                                </p>
                            )}
                        </div>
                        {showChevron && (
                            <IonIcon
                                icon={chevronForwardOutline}
                                style={{
                                    fontSize: 20,
                                    color: variant === 'hero' ? 'rgba(255,255,255,0.5)' : 'var(--ion-color-medium)',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </div>
                )}

                {/* Content */}
                {children}
            </div>
        </Element>
    );
};

// ═══════════════════════════════════════════════════════════════════
// STAT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const StatCard: React.FC<StatCardProps> = ({
    primaryValue,
    primarySentiment = 'neutral',
    stats,
    progress,
    variant = 'default',
    size = 'md',
    ...cardProps
}) => {
    const sizeConfig = SIZE_CONFIG[size];

    const formatValue = (val: string | number): string => {
        if (typeof val === 'number') {
            // Use absolute value - color indicates positive/negative, not the sign
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(Math.abs(val));
        }
        return val;
    };

    const getProgressColor = (value: number): string => {
        if (progress?.color && progress.color !== 'auto') {
            return progress.color;
        }
        // Auto color based on utilization
        if (value > 0.7) return 'danger';
        if (value > 0.3) return 'warning';
        return 'success';
    };

    return (
        <Card variant={variant} size={size} {...cardProps}>
            {/* Primary Value */}
            <div style={{
                fontFamily: 'var(--kff-data-font)',
                fontSize: size === 'lg' ? 32 : size === 'md' ? 28 : 24,
                fontWeight: 600,
                color: variant === 'hero'
                    ? (typeof primaryValue === 'number' && primaryValue < 0 ? '#fca5a5' : '#ffffff')
                    : SENTIMENT_COLORS[primarySentiment],
                marginBottom: stats || progress ? 16 : 0,
                fontFeatureSettings: '"tnum"', // Tabular figures
            }}>
                {formatValue(primaryValue)}
            </div>

            {/* Stats Row */}
            {stats && stats.length > 0 && (
                <div style={{
                    display: 'flex',
                    gap: 24,
                    flexWrap: 'wrap',
                }}>
                    {stats.map((stat, idx) => (
                        <div key={idx} style={{flex: '1 1 0', minWidth: 80}}>
                            <p style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: sizeConfig.subtitleSize,
                                color: variant === 'hero' ? 'rgba(255,255,255,0.7)' : 'var(--ion-color-medium)',
                                margin: '0 0 4px',
                            }}>
                                {stat.label}
                            </p>
                            <p style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: sizeConfig.titleSize,
                                fontWeight: 600,
                                color: variant === 'hero'
                                    ? stat.sentiment === 'positive' ? '#86efac' : stat.sentiment === 'negative' ? '#fca5a5' : '#ffffff'
                                    : SENTIMENT_COLORS[stat.sentiment || 'neutral'],
                                margin: 0,
                                fontFeatureSettings: '"tnum"',
                            }}>
                                {formatValue(stat.value)}
                            </p>
                            {stat.sublabel && (
                                <p style={{
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: 10,
                                    color: variant === 'hero' ? 'rgba(255,255,255,0.5)' : 'var(--ion-color-step-500)',
                                    margin: '2px 0 0',
                                }}>
                                    {stat.sublabel}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Progress Bar */}
            {progress && (
                <div style={{marginTop: stats ? 16 : 0}}>
                    <IonProgressBar
                        value={progress.value}
                        color={getProgressColor(progress.value)}
                        style={{height: 6, borderRadius: 3}}
                    />
                    {progress.label && (
                        <p style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 11,
                            color: variant === 'hero' ? 'rgba(255,255,255,0.6)' : 'var(--ion-color-medium)',
                            margin: '6px 0 0',
                        }}>
                            {progress.label}
                        </p>
                    )}
                </div>
            )}
        </Card>
    );
};

// ═══════════════════════════════════════════════════════════════════
// ACCOUNT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════

const ACCOUNT_ICONS: Record<string, string> = {
    checking: 'wallet-outline',
    savings: 'cash-outline',
    credit: 'card-outline',
    loan: 'business-outline',
    investment: 'trending-up-outline',
};

const ACCOUNT_COLORS: Record<string, CardAccent> = {
    checking: 'primary',
    savings: 'success',
    credit: 'danger',
    loan: 'gold',
    investment: 'transfer',
};

export const AccountCard: React.FC<AccountCardProps> = ({
    name,
    mask,
    institution,
    logoUrl,
    accountType = 'checking',
    accountSubtype,
    balance,
    creditLimit,
    originalBalance,
    interestRate,
    apy,
    isDebt,
    size = 'md',
    onClick,
    ...cardProps
}) => {
    const [logoError, setLogoError] = useState(false);
    const sizeConfig = SIZE_CONFIG[size];

    const showLogo = logoUrl && !logoError;
    // HELOCs are revolving credit like credit cards - show utilization if they have a credit limit
    const isHeloc = accountType === 'loan' && accountSubtype === 'heloc';
    // For HELOCs, use creditLimit or fallback to originalBalance as the credit line
    const helocLimit = isHeloc ? (creditLimit || originalBalance) : null;
    const utilization = creditLimit
        ? Math.abs(balance) / creditLimit
        : (isHeloc && originalBalance ? Math.abs(balance) / originalBalance : null);
    // Loan payoff progress: percentage of original balance that has been paid off
    // Don't show payoff progress for HELOCs (they use utilization instead)
    const payoffProgress = originalBalance && originalBalance > 0 && !isHeloc
        ? (originalBalance - Math.abs(balance)) / originalBalance
        : null;
    const shouldShowAsDebt = isDebt ?? ['credit', 'loan'].includes(accountType);

    const formatCurrency = (val: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(val);
    };

    // Abbreviated currency for limits (e.g., $480k instead of $480,000.00)
    const formatCurrencyAbbrev = (val: number): string => {
        const absVal = Math.abs(val);
        if (absVal >= 1000000) {
            return `$${(absVal / 1000000).toFixed(absVal % 1000000 === 0 ? 0 : 1)}M`;
        }
        if (absVal >= 1000) {
            return `$${(absVal / 1000).toFixed(absVal % 1000 === 0 ? 0 : 0)}k`;
        }
        return formatCurrency(val);
    };

    return (
        <Card
            variant="default"
            size={size}
            interactive
            onClick={onClick}
            showChevron
            {...cardProps}
        >
            {/* Header with logo/icon and name */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 20,
            }}>
                {showLogo ? (
                    <img
                        src={logoUrl}
                        alt={institution || name}
                        onError={() => setLogoError(true)}
                        style={{
                            width: sizeConfig.iconBadgeSize,
                            height: sizeConfig.iconBadgeSize,
                            objectFit: 'contain',
                            borderRadius: sizeConfig.borderRadius - 6,
                            background: '#fff',
                            padding: 4,
                        }}
                    />
                ) : (
                    <div style={{
                        width: sizeConfig.iconBadgeSize,
                        height: sizeConfig.iconBadgeSize,
                        borderRadius: sizeConfig.borderRadius - 6,
                        background: ACCENT_COLORS[ACCOUNT_COLORS[accountType] || 'primary'],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <IonIcon
                            icon={ACCOUNT_ICONS[accountType] || 'wallet-outline'}
                            style={{fontSize: sizeConfig.iconSize, color: '#ffffff'}}
                        />
                    </div>
                )}
                <div style={{flex: 1, minWidth: 0}}>
                    <h4 style={{
                        fontFamily: 'var(--kff-heading-font)',
                        fontSize: sizeConfig.titleSize - 1,
                        fontWeight: 600,
                        margin: 0,
                        color: 'var(--ion-text-color)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.3,
                    }}>
                        {name}
                        {mask && (
                            <span style={{
                                fontWeight: 400,
                                color: 'var(--ion-color-medium)',
                                fontSize: sizeConfig.subtitleSize,
                                marginLeft: 6,
                            }}>
                                ••{mask}
                            </span>
                        )}
                    </h4>
                    {institution && (
                        <p style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: sizeConfig.subtitleSize,
                            color: 'var(--ion-color-medium)',
                            margin: '2px 0 0',
                        }}>
                            {institution}
                        </p>
                    )}
                </div>
            </div>

            {/* Balance */}
            <div style={{marginBottom: utilization !== null || payoffProgress !== null ? 12 : 0}}>
                <span style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: size === 'lg' ? 24 : size === 'md' ? 22 : 18,
                    fontWeight: 600,
                    color: shouldShowAsDebt ? 'var(--ff-expense)' : 'var(--ff-income)',
                    fontFeatureSettings: '"tnum"',
                }}>
                    {formatCurrency(Math.abs(balance))}
                </span>
                {/* Credit limit for credit cards and HELOCs */}
                {shouldShowAsDebt && creditLimit && (
                    <span style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizeConfig.subtitleSize,
                        color: 'var(--ion-color-medium)',
                        marginLeft: 8,
                    }}>
                        / {formatCurrencyAbbrev(creditLimit)}
                    </span>
                )}
                {/* For HELOCs without creditLimit, show originalBalance as the credit line */}
                {shouldShowAsDebt && isHeloc && !creditLimit && originalBalance && (
                    <span style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizeConfig.subtitleSize,
                        color: 'var(--ion-color-medium)',
                        marginLeft: 8,
                    }}>
                        / {formatCurrencyAbbrev(originalBalance)}
                    </span>
                )}
                {/* Original balance for regular loans (not HELOCs) */}
                {shouldShowAsDebt && originalBalance && !creditLimit && !isHeloc && (
                    <span style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizeConfig.subtitleSize,
                        color: 'var(--ion-color-medium)',
                        marginLeft: 8,
                    }}>
                        / {formatCurrencyAbbrev(originalBalance)}
                    </span>
                )}
            </div>

            {/* Credit utilization bar */}
            {utilization !== null && (
                <div>
                    <IonProgressBar
                        value={utilization}
                        color={utilization > 0.7 ? 'danger' : utilization > 0.3 ? 'warning' : 'success'}
                        style={{height: 6, borderRadius: 3}}
                    />
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 6,
                    }}>
                        <p style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 11,
                            color: 'var(--ion-color-medium)',
                            margin: 0,
                        }}>
                            {Math.round(utilization * 100)}% utilized
                        </p>
                        {interestRate && (
                            <p style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 11,
                                color: 'var(--ion-color-medium)',
                                margin: 0,
                            }}>
                                {interestRate}% APR
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Loan payoff progress bar */}
            {payoffProgress !== null && (
                <div>
                    <IonProgressBar
                        value={payoffProgress}
                        color={payoffProgress > 0.7 ? 'success' : payoffProgress > 0.3 ? 'warning' : 'danger'}
                        style={{height: 6, borderRadius: 3}}
                    />
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 6,
                    }}>
                        <p style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 11,
                            color: 'var(--ion-color-medium)',
                            margin: 0,
                        }}>
                            {Math.round(payoffProgress * 100)}% paid off
                        </p>
                        {interestRate && (
                            <p style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 11,
                                color: 'var(--ion-color-medium)',
                                margin: 0,
                            }}>
                                {interestRate}% APR
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Interest rate for loans without original balance */}
            {interestRate && !creditLimit && !originalBalance && (
                <p style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: sizeConfig.subtitleSize,
                    color: 'var(--ion-color-medium)',
                    margin: 0,
                }}>
                    {interestRate}% APR
                </p>
            )}

            {/* APY for non-debt accounts (checking/savings/investment) */}
            {!shouldShowAsDebt && !utilization && !payoffProgress && apy != null && apy > 0 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 8,
                }}>
                    <span style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 11,
                        color: 'var(--ion-color-medium)',
                    }}>
                        {apy.toFixed(2)}% APY
                    </span>
                </div>
            )}
        </Card>
    );
};

Card.displayName = 'Card';
StatCard.displayName = 'StatCard';
AccountCard.displayName = 'AccountCard';

export default Card;
